<script>
import BaseView from "@/views/BaseView.vue";
import { mapState } from "pinia";
import { useAppStore } from "@/store/app.store";
import { DispatcherPermissionValue } from "@/api/dto/dispatcher.dto.ts";

export default {
  name: "SidebarComponent",
  mixins: [BaseView],
  props: {
    isCollapsed: {
      type: Boolean,
      default: true,
    },
    openThreadsCount: Number,
  },
  computed: {
    ...mapState(useAppStore, ["dispatcher"]),
    menu() {
      const items = [];

      if (this.hasAccess(DispatcherPermissionValue.ViewChat)) {
        items.push({
          icon: "mdi-message-outline",
          title: this.$t("chats.chat"),
          to: { name: "chat" },
          count: this.openThreadsCount,
        });
      }

      if (this.hasAccess(DispatcherPermissionValue.ViewUsers)) {
        items.push({
          icon: "mdi-account-multiple-outline",
          items: [
            {
              title: this.$t("drivers.drivers"),
              to: { name: "drivers" },
            },
            {
              title: this.$t("drivers.owners"),
              to: { name: "owners" },
            },
            {
              title: this.$t("drivers.coordinators"),
              to: { name: "coordinators" },
            },
          ],
          title: this.$t("users.users"),
          url: "/users",
        });
      }

      const tripsSubMenu = [];

      if (this.hasAccess(DispatcherPermissionValue.ViewOrders)) {
        tripsSubMenu.push({
          title: "Manage orders",
          to: { name: "orders" },
        });
      }

      if (this.hasAccess(DispatcherPermissionValue.ViewTripMonitor)) {
        tripsSubMenu.push({
          title: "Trip monitor",
          to: { name: "trip-monitor" },
        });
      }

      if (tripsSubMenu.length > 0) {
        items.push({
          icon: "mdi-package-variant-closed",
          title: "Trips",
          items: tripsSubMenu,
        });
      }

      // Fleets: trucks, trailers
      if (this.hasAccess(DispatcherPermissionValue.ViewTrucks) || this.hasAccess(DispatcherPermissionValue.AccessToTruckSearch)) {
        const subMenu = []
        if (this.hasAccess(DispatcherPermissionValue.ViewTrucks)) {
          subMenu.push({
            title: this.$t("trucks.trucks"),
            to: { name: "trucks" },
          });
          subMenu.push({
            title: this.$t("trucks.history"),
            to: { name: "trucks-history" },
          });
        }


        if (this.hasAccess(DispatcherPermissionValue.AccessToTruckSearch)) {
          subMenu.push({
            title: this.$t("trucks.search"),
            to: { name: "trucks-search" },
          });
        }

        items.push({
          icon: "mdi-truck-outline",
          items: subMenu,
          title: "Fleet",
          url: "/fleets",
        });
      }

      if (this.hasAccess(DispatcherPermissionValue.ViewCompanies)) {
        items.push({
          icon: "mdi-domain",
          items: [
            {
              title: this.$t("company.carriers"),
              to: { name: "carriers" },
            },
            {
              title: this.$t("company.customers"),
              to: { name: "customers" },
            },
            {
              title: this.$t("company.facilities"),
              to: { name: "facilities" },
            },
          ],
          title: this.$t("company.companies"),
          url: "/company",
        });
      }

      if (this.hasAccess(DispatcherPermissionValue.AccessToBilling)) {
        items.push({
          icon: "mdi-currency-usd",
          items: [
            {
              title: this.$t("billing.reports"),
              to: { name: "billing-reports" },
            },
          ],
          title: this.$t("billing.billing"),
          url: "/billing",
        });
      }

      if (this.hasAccess(DispatcherPermissionValue.ViewDispatchers)) {
        items.push(
          {
            icon: "mdi-clipboard-text-outline",
            title: this.$t("dispatchers.logs"),
            to: { name: "logs" },
          },
          {
            icon: "mdi-account-circle-outline",
            to: { name: "dispatchers" },
            title: this.$t("dispatchers.dispatchers"),
          },
        );
      }

      return items;
    },
  },
  methods: {
    hasActiveChild(item) {
      return item.items.some((child) => child.to.name === this.$route.name);
    },
  },
};
</script>

<template>
  <v-navigation-drawer id="main-sidebar" :permanent="true" app class="pt-3 pb-8 dark-background" width="250">
    <div class="sidebar-navigation d-flex flex-column justify-sm-space-between height-100">
      <div>
        <!-- Sidebar header -->
        <div
          :class="{ 'justify-center': isCollapsed }"
          class="sidebar-nav__header pt-3 pb-3 mb-9 d-flex px-4 align-center justify-center"
        >
          <img
            v-if="dispatcher?.company?.code"
            :src="getAssetUrl('logo/' + dispatcher.company.code + '.svg')"
            alt="Logo"
            class="company-logo"
          />
          <span v-if="!isCollapsed" class="company-name ml-2">{{ dispatcher?.company.name }}</span>
        </div>
        <div
          :class="{ 'pl-4': !isCollapsed, 'text-center': isCollapsed }"
          class="text-uppercase caption font-weight-600 text-nav"
        >
          Menu
        </div>

        <!-- Sidebar navigation -->
        <v-list density="compact">
          <template v-for="(item, i) in menu">
            <!-- Collapsed sidebar navigation -->
            <v-menu
              v-if="item.items && isCollapsed"
              :key="'menu-item-' + i"
              content-class="sidebar-submenu"
              location="right"
              tile
              transition="slide-x-transition"
            >
              <template #activator="{ props }">
                <v-list-item :class="{ 'v-list-item--active': hasActiveChild(item) }" v-bind="props">
                  <template v-if="item.icon" #prepend>
                    <v-icon :color="item.color" size="20">
                      {{ item.icon }}
                    </v-icon>
                  </template>
                  <v-list-item-title class="pa-0">
                    {{ item.title }}
                  </v-list-item-title>
                </v-list-item>
              </template>

              <v-list>
                <v-list-item v-for="(child, j) in item.items" :key="j" :link="true" :to="child.to">
                  <v-list-item-title class="pa-0">
                    {{ child.title }}
                  </v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
            <!-- Full sidebar navigation -->
            <v-list-group v-else-if="item.items && !isCollapsed" :key="i" v-model="item.active" :group="item.url">
              <template #activator="{ props }">
                <v-list-item v-bind="props">
                  <template v-if="item.icon" #prepend>
                    <v-icon :color="item.color" size="20">
                      {{ item.icon }}
                    </v-icon>
                  </template>
                  <v-list-item-title class="pa-0">
                    {{ item.title }}
                  </v-list-item-title>
                </v-list-item>
              </template>

              <v-list-item v-for="(child, m) in item.items" :key="m" :link="true" :to="child.to">
                <v-list-item-title class="pa-0">
                  {{ child.title }}
                </v-list-item-title>
              </v-list-item>
            </v-list-group>
            <v-list-item v-else :key="item.title" :link="true" :to="item.to">
              <template v-if="item.icon" #prepend>
                <v-icon :color="item.color" size="20">
                  {{ item.icon }}
                </v-icon>
              </template>
              <v-list-item-title>
                {{ item.title }}
              </v-list-item-title>
            </v-list-item>
          </template>
        </v-list>
      </div>
      <div v-if="!isCollapsed" class="pl-5 pr-5 text-center text-body-2 mt-4 text-nav">&copy; Omni Dispatch, 2023</div>
    </div>
  </v-navigation-drawer>
</template>

<style lang="scss" scoped>
@import "@/assets/style/color";

.sidebar-navigation {
  .company-logo {
    width: 26px;
    height: 26px;
  }

  .company-name {
    font-size: 16px;
    font-weight: 500;
    color: $white;
  }

  .caption {
    font-size: 11px;
  }

  .v-list-item {
    .v-list-item-title {
      color: $nav;
    }

    .v-icon {
      color: $nav;
    }

    :deep(.v-list-item__append) {
      .v-icon {
        color: $nav;
      }
    }

    :deep(.v-list-item__prepend) {
      display: block;
      text-align: center;
    }

    &--active {
      background-color: $dark-nav;

      &:before {
        content: "";
        border-left: 3px solid $primary;
        position: absolute;
        height: 100%;
      }

      .v-list-item-title {
        color: $white;
      }

      .v-icon {
        color: $white;
      }

      :deep(.v-list-item__append) {
        .v-icon {
          color: $white;
        }
      }
    }
  }

  .v-list-group--open {
    .v-list-group__items {
      .v-list-item--active {
        &:before {
          border-left: none;
        }
      }
    }
  }

  .v-list-group__items {
    .v-list-item {
      padding-inline-start: 52px !important;
    }
  }
}
</style>
